import { useEffect, useRef, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import ClientCookies from 'js-cookie'
import { useIsMember, useUserId } from '@/features/account/services/User/hooks'
import { useCouponCode } from '@/features/authentication/services/SignupPromotions/hooks'
import { getHasSignupPromotionPlans } from '@/features/authentication/services/SignupPromotions/utils'
import { useHasSignupPromotionQueryParam } from '@/features/shop/components/RetailerHomepage/hooks'
import { useQuerySignupPlans } from '@/features/shop/services/DeliveryPlans/queries'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { getSearchQueryString } from '@/features/shared/utils/url'
import { loginCompletedTriggerCookieName } from '@shared/constants/auth'
import { routes } from '@shared/constants/routes'
import { pciRouteDictionary } from '@shared/constants/routes.pci'

const CHOOSE_MEMBERSHIP_PATHS = new Set<string>([
  routes.CHOOSE_MEMBERSHIP.url,
  pciRouteDictionary.SECURE_CHOOSE_MEMBERSHIP_PAYMENT.url,
])

export const useGlobalSignupPromoRedirect = () => {
  const router = useSegwayRouter()
  const pathname = usePathname() ?? ''
  const searchParams = useSearchParams()

  const userId = useUserId()
  const isMember = useIsMember()
  const { coupon } = useCouponCode()
  const hasSignupPromotionQueryParam = useHasSignupPromotionQueryParam()

  const isOnGhp = pathname === routes.GLOBAL_HOMEPAGE.url
  const isOnChooseMembership = CHOOSE_MEMBERSHIP_PATHS.has(pathname)

  // snapshot na_lc once on mount instead of reading it live. useHandleLoginCompletion deletes the
  // cookie as soon as userId loads, which races ahead of the async useQuerySignupPlans we depend on,
  // so a live read would flip this to false mid check and the redirect would never fire
  const [wasJustLoggedIn, setWasJustLoggedIn] = useState(
    () => ClientCookies.get(loginCompletedTriggerCookieName) === '1',
  )

  // guards the promo redirect so it fires at most once. if the choose membership page ever
  // bounces the user back to GHP, this ref stops us from re pushing and spinning into an
  // infinite loop.
  const hasRedirectedRef = useRef(false)

  // this is strict parity with the old `useShouldRedirectToChooseMembership` hook
  // that ran on GHP. it covers already logged in arrivals like /onepass → /signup → /shop?employee_id=X.
  const isOnGHPWithSignupPromotionQueryParam = isOnGhp && hasSignupPromotionQueryParam

  const triggerScope = isOnGHPWithSignupPromotionQueryParam || wasJustLoggedIn

  const eligibleForRedirect = Boolean(triggerScope && userId && !isMember && !isOnChooseMembership)
  const {
    isError,
    isSuccess,
    data: signupPlans,
  } = useQuerySignupPlans({ enabled: eligibleForRedirect })

  const hasCouponPlan = Boolean(coupon?.code_group.id)
  const hasPromoPlan = getHasSignupPromotionPlans(signupPlans)
  // this preserves the behavior where gift_code URL param always wins, even
  // if the coupon lookup returned nothing. the choose membership page surfaces the
  // "invalid gift code" UI if there is an error with the gift code.
  const hasGiftCodeParam = Boolean(searchParams?.get('gift_code'))
  const shouldRedirect = eligibleForRedirect && (hasPromoPlan || hasCouponPlan || hasGiftCodeParam)

  // redirect decision resolved once user data is loaded and we are either not eligible
  // or the plans query has settled. until then we hold wasJustLoggedIn state so we don't
  // close the gate before the data lands
  const decisionResolved = Boolean(userId) && (!eligibleForRedirect || isSuccess || isError)

  // consume the wasJustLoggedIn state as soon as the decision resolves with no redirect.
  // guarded so this runs once: wasJustLoggedIn can only ever go true -> false here
  if (wasJustLoggedIn && decisionResolved && !shouldRedirect) {
    setWasJustLoggedIn(false)
  }

  useEffect(() => {
    if (!shouldRedirect || hasRedirectedRef.current) return
    hasRedirectedRef.current = true
    router.push(
      `${routes.CHOOSE_MEMBERSHIP.url}${searchParams ? getSearchQueryString(searchParams) : ''}`,
    )
  }, [shouldRedirect, router, searchParams])
}
